import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Button, Input, Table, Popconfirm, InputNumber, Checkbox, Select } from 'antd';
import NewStyleInput from './NewStyleInput';
import Tooltip from '../../common/Tooltip';
import { useCallback, useState } from 'react';
import { useStore } from '../../../store/store';
import { ColorPaletteButton } from '../../common/ColorPaletteButton';
import LabelledRow from '../../common/LabelledRow';
import FieldGroupLabel from '../../common/boxStyleFields/FieldGroupLabel';
import DuotoneLightIcon from '../../common/DuotoneLightIcon';
import './tableConfig.css';
import { ItemTypes, defaultCheckboxConfiguration, dispostionButtons, styleCheckbox } from '../../../constants/constants';
import { useTranslation } from 'react-i18next';
import { maxFontSize, minFontSize } from '../../../constants/gridConfig';
import { TextStyleButtons } from '../../common/TitleConfiguration';
import { iconButtonStyle, mergeConfigurationCheckboxStyles, navbarButtonStyle } from '../../../utils/styles';
import { NavbarListButton } from '../../Layout/NavbarListButton';
import FontPicker from '../../common/FontPicker';
import { useSectionStylesFilter } from './utils';
import { useShareWithConfigPreview } from '../../../contexts/ConfigPreviewContext';

const selector = ({
  templates,
  configuration,
  setSelectedStyle,
  currentTemplate,
  createTemplateInStore,
  setConfiguration,
  postTemplate,
  tmp
}) => ({
  checkboxStyles: templates.filter((t) => t.pattern === 'style'),
  templates,
  configuration,
  setSelectedStyle,
  templatable: currentTemplate.templatable,
  createTemplateInStore,
  setConfiguration,
  postTemplate,
  tmp
});

export const TabsCheckbox = () => {
  const { configuration, setConfiguration, tmp } = useStore(selector);
  const [selectedStyle, setSelectedStyle] = useState(mergeConfigurationCheckboxStyles(configuration, tmp)[0]);
  // Style montré sur la page d'aperçu du Drawer.
  useShareWithConfigPreview('checkboxStyleId', selectedStyle?.id);
  const [isEditingStyleName, setIsEditingStyleName] = useState(false);
  const { t } = useTranslation();
  let styleList = [...mergeConfigurationCheckboxStyles(configuration, tmp)];
  const { visibleStyles, filter: sectionStylesFilter } =
    useSectionStylesFilter(styleList);
  const styleData = [
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      width: (18 + 16) * 3 + 16,
    },
    {
      title: 'Style',
      dataIndex: 'style',
      key: 'style',
      onCell: (element, rowIndex) => {
        return {
          key: `style-config-${rowIndex}`,
          onClick: (event) => {
            handleSelectedStyle(element);
          },
        };
      },
    }
  ];

  const handleRemoveStyle = (idToRemove) => {
    const newCheckboxStyles = configuration.checkboxStyles.filter(style => style.id !== idToRemove);
    setConfiguration({
      ...configuration.checkboxStyles,
      checkboxStyles: newCheckboxStyles,
    }, (draft) => {
      draft.boxes.map((b) => {
        if ((b.type === ItemTypes.CHECKBOXS_VARIABLE || b.type === ItemTypes.QUESTION) && b.checkboxStyleId === idToRemove) {
          b.checkboxStyleId = draft.configuration.checkboxStyles[0].id
        }
        return b
      })
    });
  };

  const handleRenameStyle = (ID, event) => {
    let styleToRename = {
      ...configuration.checkboxStyles.find((element) => element.id === ID),
      name: event.target.value,
    };
    setConfiguration({
      checkboxStyles: [
        ...configuration.checkboxStyles.map((tableStyle) => {
          if (tableStyle.id === ID) {
            return styleToRename;
          }
          return tableStyle;
        }),
      ],
    });
    setIsEditingStyleName(null);
    setEditedName('');
    setSelectedStyle(styleToRename);
  };

  const handleCopyStyle = (copiedId) => {
    const copiedStyle = configuration.checkboxStyles.find((s) => s.id === copiedId);
    let newStyle = {
      ...copiedStyle,
      id: configuration.checkboxStylesIdCount + 1,
      name: `${copiedStyle.name} - Copie`,
    };
    let checkboxStyles = [
      ...configuration.checkboxStyles,
      newStyle,
    ];

    setConfiguration({
      ...configuration,
      checkboxStyles,
      checkboxStylesIdCount: configuration.checkboxStylesIdCount + 1,
    });
  };

  const [editedName, setEditedName] = useState('');
  const dataSourceMapped = [];

  const iconStyle = {
    margin: '.5rem',
    fontSize: '1.5em',
  }
  visibleStyles.forEach((element) => {
    const cantEdit = element.id === 0 || element.fromTmp === true;
    const checkboxStyleLocked = element.fromTmp === true;
    dataSourceMapped.push({
      key: `tableStyle-${element.id}`,
      id: element.id,
      style:
        isEditingStyleName === element.id ? (
          <Input
            value={editedName}
            id='styleNameInput2'
            style={{ width: '60%' }}
            onChange={(e) => setEditedName(e.target.value)}
            onPressEnter={(e) => {
              if (element.id !== 0 && e.target.value.length > 0) {
                handleRenameStyle(element.id, e);
              }
            }}
            onBlur={(e) => {
              if (!cantEdit && e.target.value.length > 0) {
                handleRenameStyle(element.id, e);
              }
            }}
          />
        ) : (
          element.name
        ),
      actions: (
        <div>
          <Tooltip
            placement='bottom'
            mouseLeaveDelay='0'
            title={'Renommer'}
          >
            <FontAwesomeIcon
              icon={icon({ name: 'edit', style: 'light' })}
              style={{
                ...iconStyle,
                color: cantEdit ? 'lightgray' : 'black',
              }}
              onClick={cantEdit ? null : (e) => {
                setIsEditingStyleName(element.id);
                setEditedName(element.name);
              }}
            />
          </Tooltip>
          <Tooltip
            placement='bottom'
            mouseLeaveDelay='0'
            title={'Dupliquer'}
          >
            <FontAwesomeIcon
              icon={icon({ name: 'copy', style: 'light' })}
              style={{
                ...iconStyle,
                color: checkboxStyleLocked ? 'lightgray' : 'black',
              }}
              onClick={checkboxStyleLocked ? null : (e) => handleCopyStyle(element.id)}
            />
          </Tooltip>
          <Tooltip
            placement='bottom'
            mouseLeaveDelay='0'
            title={'Supprimer'}
          >
            {cantEdit ? (
              <FontAwesomeIcon
                icon={icon({ name: 'trash', style: 'light' })}
                style={{
                  ...iconStyle,
                  color: 'lightgray'
                }}
              />
            ) : (
              <Popconfirm
                title='Voulez vous vraiment supprimer ce style ?'
                onConfirm={() => { handleRemoveStyle(element.id); }}
                okText='Oui'
                cancelText='Non'
              >
                <FontAwesomeIcon
                  icon={icon({ name: 'trash', style: 'light' })}
                  style={{
                    ...iconStyle,
                    color: 'red'
                  }}
                />
              </Popconfirm>
            )}
          </Tooltip>
        </div>
      ),
    });
  });

  const [isAddTableStyleLoading, setIsAddTableStyleLoading] = useState(false);

  const handleSelectedStyle = (e) => {
    mergeConfigurationCheckboxStyles(configuration, tmp).forEach((element) => {
      if (element.id === e.id) {
        setSelectedStyle(element);
      }
    });
    return selectedStyle;
  };

  const loadingAnimation = () => {
    setIsAddTableStyleLoading(true);
    setTimeout(() => {
      setIsAddTableStyleLoading(false);
    }, 1000);
  };

  // const handleChange
  const handleSaveChanges = useCallback((updatedTableStyle) => {
    const updatedTableStyleIndex = configuration.checkboxStyles.findIndex(
      (element) => element.id === updatedTableStyle.id
    );
    let newCheckboxStyles = [...configuration.checkboxStyles];
    newCheckboxStyles[updatedTableStyleIndex] = updatedTableStyle;
    setSelectedStyle(updatedTableStyle);
    setConfiguration({
      ...configuration.checkboxStyles,
      checkboxStyles: newCheckboxStyles
    });
  }, [configuration?.checkboxStyles, setConfiguration]);

  const handleAddTableStyle = (newStyleName) => {
    loadingAnimation();
    const newStyle = {
      ...defaultCheckboxConfiguration,
      id: configuration.checkboxStylesIdCount,
      name: newStyleName,
      fontFamily: configuration.fontFamily,
    };
    let checkboxStyles = [
      ...configuration.checkboxStyles,
      newStyle,
    ];
    setConfiguration({
      ...configuration,
      checkboxStyles,
      checkboxStylesIdCount: configuration.checkboxStylesIdCount + 1,
    });
    setSelectedStyle({ ...newStyle });
  };

  const handleChangeFontSize = useCallback(() => (value) => {

    handleSaveChanges({
      ...selectedStyle,
      fontSize: value,
    });
  }, [handleSaveChanges, selectedStyle]);
  const handleChangeFontWeight = useCallback(() => () => {
    handleSaveChanges({
      ...selectedStyle,
      fontWeight: selectedStyle?.fontWeight ? '' : 'bold',
    });
  }, [handleSaveChanges, selectedStyle]);
  const handleChangeFontStyle = useCallback(() => () => {
    handleSaveChanges({
      ...selectedStyle,
      fontStyle: selectedStyle?.fontStyle ? '' : 'italic',
    });
  }, [handleSaveChanges, selectedStyle]);
  const handleChangeFontDecoration = useCallback(() => () => {
    handleSaveChanges({
      ...selectedStyle,
      textDecoration: selectedStyle?.textDecoration ? '' : 'underline',
    });
  }, [handleSaveChanges, selectedStyle]);

  const handleChangeStyleBox = useCallback(() => (value) => {
    handleSaveChanges({
      ...selectedStyle,
      styleBox: value,
    });
  }, [handleSaveChanges, selectedStyle]);

  const handleChangeColor = useCallback(() => (value) => {
    handleSaveChanges({
      ...selectedStyle,
      color: { "label": value, "style": configuration.colors.find((color) => color.label === value).style },
    });
  }, [handleSaveChanges, selectedStyle, configuration]);

  const handleChangeBackgroundColor = useCallback(() => (value) => {
    handleSaveChanges({
      ...selectedStyle,
      backgroundColor: { "label": value, "style": configuration.colors.find((color) => color.label === value).style },
    });
  }, [handleSaveChanges, selectedStyle, configuration]);

  const handleChangeBoxBackgroundColor = useCallback(() => (value) => {
    handleSaveChanges({
      ...selectedStyle,
      box: {
        ...selectedStyle.box,
        backgroundColor: { "label": value, "style": configuration.colors.find((color) => color.label === value).style }
      }
    });
  }, [handleSaveChanges, selectedStyle, configuration]);

  const handleChangeSizeBox = useCallback(() => (value) => {
    handleSaveChanges({
      ...selectedStyle,
      box: {
        ...selectedStyle.box,
        width: value,
        height: value,
      }
    });
  }, [handleSaveChanges, selectedStyle]);

  const handleChangeDirectionLabel = useCallback(() => (value) => {
    handleSaveChanges({
      ...selectedStyle,
      box: {
        ...selectedStyle.box,
        direction: value.target.checked ? "rtl" : "ltr",
      }
    });
  }, [handleSaveChanges, selectedStyle]);

  const handleChangeBorderColor = useCallback(() => (value) => {
    handleSaveChanges({
      ...selectedStyle,
      borders: {
        ...selectedStyle.borders,
        ...{ color: { "label": value, "style": configuration.colors.find((color) => color.label === value).style } }
      },
    });
  }, [handleSaveChanges, selectedStyle, configuration]);

  const handleChangeBorderSize = useCallback(() => (value) => {
    handleSaveChanges({
      ...selectedStyle,
      borders: {
        ...selectedStyle.borders,
        size: value,
      },
    });
  }, [handleSaveChanges, selectedStyle]);

  const handleChangeBorderRadius = useCallback(() => (value) => {
    handleSaveChanges({
      ...selectedStyle,
      borders: {
        ...selectedStyle.borders,
        radius: value,
      },
    });
  }, [handleSaveChanges, selectedStyle]);

  const handleChangeColorCheck = useCallback(() => (value) => {
    handleSaveChanges({
      ...selectedStyle,
      check: {
        ...selectedStyle.check,
        ...{ color: { "label": value, "style": configuration.colors.find((color) => color.label === value).style } }
      },
    });
  }, [handleSaveChanges, selectedStyle, configuration]);

  const handleChangeSizeCheck = useCallback(() => (value) => {
    handleSaveChanges({
      ...selectedStyle,
      check: {
        ...selectedStyle.check,
        width: value,
        height: value,
      },
    });
  }, [handleSaveChanges, selectedStyle]);

  const handleChangeCheckWeight = useCallback(() => (value) => {
    handleSaveChanges({
      ...selectedStyle,
      check: {
        ...selectedStyle.check,
        weight: value,
      },
    });
  }, [handleSaveChanges, selectedStyle]);

  const handleChangeBorderPlace = useCallback(() => (value) => {
    if (value === 'all') {
      handleSaveChanges({
        ...selectedStyle,
        borders: {
          ...selectedStyle.borders,
          top: true,
          bottom: true,
          left: true,
          right: true,
        },
      });
    } else {
      handleSaveChanges({
        ...selectedStyle,
        borders: {
          ...selectedStyle.borders,
          [value]: !selectedStyle.borders[value],
        },
      });
    }

  }, [handleSaveChanges, selectedStyle]);

  const handleChangeDisposition = useCallback(() => (value) => {
    handleSaveChanges({
      ...selectedStyle,
      disposition: value,
    });
  }, [handleSaveChanges, selectedStyle]);
  const handleChangeMarginLabel = useCallback(() => (value) => {
    handleSaveChanges({
      ...selectedStyle,
      label: {
        ...selectedStyle.label,
        margin: value,
      }
    });
  }, [handleSaveChanges, selectedStyle]);

  const handleChangeFontFamily = useCallback(() => (value) => {
    handleSaveChanges({
      ...selectedStyle,
      fontFamily: value,
    });
  }, [handleSaveChanges, selectedStyle]);

  const changeHandler = useCallback(() => ({
    fontSize: handleChangeFontSize(),
    fontWeight: handleChangeFontWeight(),
    fontStyle: handleChangeFontStyle(),
    fontFamily: handleChangeFontFamily(),
    textDecoration: handleChangeFontDecoration(),
    color: handleChangeColor(),
    backgroundColor: handleChangeBackgroundColor(),
    borderColors: handleChangeBorderColor(),
    borderPlace: handleChangeBorderPlace(),
    sizeBox: handleChangeSizeBox(),
    styleBox: handleChangeStyleBox(),
    colorCheck: handleChangeColorCheck(),
    sizeCheck: handleChangeSizeCheck(),
    borderSize: handleChangeBorderSize(),
    borderRadius: handleChangeBorderRadius(),
    disposition: handleChangeDisposition(),
    marginLabel: handleChangeMarginLabel(),
    directionLabel: handleChangeDirectionLabel(),
    checkWeight: handleChangeCheckWeight(),
    changeBoxBackgroundColor: handleChangeBoxBackgroundColor()
  }), [
    handleChangeFontSize,
    handleChangeFontWeight,
    handleChangeFontDecoration,
    handleChangeFontStyle,
    handleChangeColor,
    handleChangeBackgroundColor,
    handleChangeBorderColor,
    handleChangeBorderPlace,
    handleChangeSizeBox,
    handleChangeStyleBox,
    handleChangeColorCheck,
    handleChangeSizeCheck,
    handleChangeBorderSize,
    handleChangeBorderRadius,
    handleChangeDisposition,
    handleChangeMarginLabel,
    handleChangeDirectionLabel,
    handleChangeCheckWeight,
    handleChangeFontFamily,
    handleChangeBoxBackgroundColor
  ])

  return (
    <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
      <div style={{ textAlign: 'center', width: "40%" }}>
        <h5>Liste des styles de cases à cocher</h5>
        <NewStyleInput onAdd={handleAddTableStyle} loading={isAddTableStyleLoading} />
        {sectionStylesFilter}
        <Table
          columns={styleData}
          dataSource={dataSourceMapped}
          rowClassName={(record, _rowIndex) => { return record.style === selectedStyle.name ? 'tableSelectedRow' : '' }}
          pagination={false}
        />
      </div>
      <div style={{ display: "flex", flexDirection: "column", width: "60%", margin: 20 }}>
        <div style={{ width: "100%" }} key={selectedStyle?.id}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
            <FieldGroupLabel>Texte</FieldGroupLabel>
            <LabelledRow label="Police des labels">
              <FontPicker
                disabled={selectedStyle.id === 0 || selectedStyle.fromTmp}
                forcedValue={selectedStyle.id === 0 ? configuration.fontFamily : selectedStyle?.fontFamily}
                onChange={(e) => changeHandler().fontFamily(e)} />
            </LabelledRow>
            <LabelledRow label={t('font-size')}>
              <InputNumber
                min={minFontSize}
                max={maxFontSize}
                disabled={selectedStyle.id === 0 || selectedStyle.fromTmp}
                defaultValue={selectedStyle.id === 0 ? configuration.fontSize : selectedStyle?.fontSize}
                onChange={(e) => changeHandler().fontSize(e)}
                style={{ marginLeft: '5px' }}
              />
            </LabelledRow>
            <LabelledRow label='Style du texte'>
              <TextStyleButtons
                rules={selectedStyle}
                changeHandler={changeHandler()}
                disabled={selectedStyle?.fromTmp}
              />
            </LabelledRow>
            <LabelledRow label="Marge du label">
              <InputNumber
                defaultValue={selectedStyle?.label?.margin}
                min={1}
                max={50}
                onChange={(e) => changeHandler().marginLabel(e)}
                disabled={selectedStyle?.fromTmp}
                style={{ marginLeft: '5px' }}
              />
            </LabelledRow>
            <LabelledRow label="Label à gauche">
              <Checkbox
                key='bold'
                style={{ fontWeight: 'bold' }}
                defaultChecked={selectedStyle?.box?.direction === 'rtl'}
                disabled={selectedStyle?.fromTmp}
                // checked={liveStylePreview()?.fontWeight !== ''}
                onChange={(e) => changeHandler().directionLabel(e)}
              />
            </LabelledRow>
            <LabelledRow label='Couleur du texte'>
              <ColorPaletteButton
                title='Couleur du texte'
                onChange={(e) => changeHandler().color(e)}
                disabled={selectedStyle.id === 0 || selectedStyle.fromTmp}
                colors={configuration.colors}
                value={selectedStyle.id === 0 ? configuration.baseFontColor : selectedStyle?.color}
              />
            </LabelledRow>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
            <FieldGroupLabel>Cases à cocher</FieldGroupLabel>
            <LabelledRow label="Taille de la case à cocher">
              <InputNumber
                defaultValue={selectedStyle?.box?.width}
                min={minFontSize}
                max={maxFontSize}
                disabled={selectedStyle?.fromTmp}
                onChange={(e) => changeHandler().sizeBox(e)}
                style={{ marginLeft: '5px' }}
              />
            </LabelledRow>
            <LabelledRow label="Taille du check">
              <InputNumber
                defaultValue={selectedStyle?.check?.width}
                min={minFontSize}
                max={maxFontSize}
                disabled={selectedStyle?.fromTmp}
                onChange={(e) => changeHandler().sizeCheck(e)}
                style={{ marginLeft: '5px' }}
              />
            </LabelledRow>
            <LabelledRow label="Style de la case à cocher">
              <Select
                disabled={selectedStyle?.fromTmp}
                options={Object.entries(styleCheckbox).map(([key, value]) => ({
                  value: key,
                  label: <FontAwesomeIcon
                    icon={value}
                  />
                }))}
                onChange={(e) => changeHandler().styleBox(e)}
                defaultValue={selectedStyle?.styleBox}
              ></Select>
            </LabelledRow>

            <LabelledRow label="Couleur de la case item">
              <ColorPaletteButton
                title="Couleur de la case item"
                icon={<DuotoneLightIcon name='check' style={iconButtonStyle()} />}
                disabled={selectedStyle?.fromTmp}
                onChange={(e) => changeHandler().colorCheck(e)}
                colors={configuration.colors}
                value={selectedStyle?.check?.color}
              />
            </LabelledRow>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
            <FieldGroupLabel>Affichage</FieldGroupLabel>


            <LabelledRow label="Couleur des bordures de la case à cocher">
              <ColorPaletteButton
                title="Couleur des bordures de la case à cocher"
                icon={<DuotoneLightIcon name='border-outer' style={iconButtonStyle()} />}
                colorInIcon='primary'
                disabled={selectedStyle?.fromTmp}
                onChange={(e) => changeHandler().borderColors(e)}
                colors={configuration.colors}
                value={selectedStyle?.borders?.color}
              />
            </LabelledRow>
            <LabelledRow label="Couleur de fond de la case à cocher">
              <ColorPaletteButton
                title="Couleur de fond de la case à cocher"
                icon={<DuotoneLightIcon name='fill-drip' style={iconButtonStyle()} />}
                disabled={selectedStyle?.fromTmp}
                onChange={(e) => changeHandler().changeBoxBackgroundColor(e)}
                colors={configuration.colors}
                value={selectedStyle?.box.backgroundColor}
              />
            </LabelledRow>
            <LabelledRow label="Taille de la bordure">
              <InputNumber
                disabled={selectedStyle?.fromTmp}
                defaultValue={selectedStyle?.borders?.size}
                min={1}
                max={10}
                onChange={(e) => changeHandler().borderSize(e)}
                style={{ marginLeft: '5px' }}
              />
            </LabelledRow>
            <LabelledRow label="Radius de la bordure">
              <InputNumber
                disabled={selectedStyle?.fromTmp}
                defaultValue={selectedStyle?.borders?.radius}
                min={1}
                max={10}
                onChange={(e) => changeHandler().borderRadius(e)}
                style={{ marginLeft: '5px' }}
              />
            </LabelledRow>
            <LabelledRow label={t('checkbox-disposition')}>
              <NavbarListButton
                title={t('checkbox-disposition')}
                icon={< FontAwesomeIcon icon={icon({ name: 'grid-dividers', style: 'light' })} style={iconButtonStyle()} />}
              >
                {Object.keys(dispostionButtons).map((buttonName) => {
                  return (
                    <Button
                      key={`${buttonName}`}
                      style={{
                        ...navbarButtonStyle,
                        marginBottom: '1px',
                      }}
                      disabled={selectedStyle?.fromTmp || false}
                      icon={dispostionButtons[buttonName].icon}
                      onClick={() => changeHandler().disposition(dispostionButtons[buttonName].disposition)}
                    />
                  );
                })}
              </NavbarListButton>
            </LabelledRow>
          </div>
        </div>
      </div>
    </div>
  );
};
export default TabsCheckbox;
